import type React from "react";
import type ReactQuill from "react-quill";

export type EmailAutomationShortCodeTarget = "subject" | "body" | null;

export type InsertEmailAutomationShortCodeOptions = {
  target: EmailAutomationShortCodeTarget;
  getSubject: () => string;
  setValue: (
    name: "subject" | "body",
    value: string,
    opts?: {shouldValidate?: boolean}
  ) => void;
  subjectInputRef: React.MutableRefObject<HTMLInputElement | null>;
  subjectCursorPos: React.MutableRefObject<number | null>;
  bodyCursorPos: React.MutableRefObject<number | null>;
  quillRef: React.MutableRefObject<ReactQuill | null>;
  /** Run after values/refs update (e.g. return to form view, clear target). */
  onAfterInsert: () => void;
};

/**
 * Inserts a short code into subject (at stored cursor) or Quill body (at stored cursor).
 * Shared by the email automations page and offering-form email automation section.
 */
export function insertEmailAutomationShortCode(
  code: string,
  options: InsertEmailAutomationShortCodeOptions
): void {
  const {
    target,
    getSubject,
    setValue,
    subjectInputRef,
    subjectCursorPos,
    bodyCursorPos,
    quillRef,
    onAfterInsert,
  } = options;

  if (target === "subject") {
    const current = getSubject();
    const pos = subjectCursorPos.current ?? current.length;
    const newValue = current.slice(0, pos) + code + current.slice(pos);
    setValue("subject", newValue, {shouldValidate: true});
    onAfterInsert();
    setTimeout(() => {
      const input = subjectInputRef.current;
      if (input) {
        const newCursorPos = pos + code.length;
        input.focus();
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 100);
    return;
  }

  if (target === "body") {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const pos = bodyCursorPos.current ?? editor.getLength() - 1;
      editor.insertText(pos, code);
      setValue("body", editor.root.innerHTML, {shouldValidate: true});
      onAfterInsert();
      setTimeout(() => {
        editor.focus();
        editor.setSelection(pos + code.length, 0);
      }, 50);
    }
  }
}
